<template>
  <div class="mini-game-list-container">
    <div class="page-header">
      <div class="page-actions">
        <el-button type="success" size="small" icon="el-icon-download" :loading="downloading" @click="handleDownloadAll">下载全部数据</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleCreate">新建小游戏</el-button>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="miniGames"
      border
      style="width: 100%"
    >
      <el-table-column prop="id" label="ID" width="80" align="center" />
      <el-table-column prop="name" label="小游戏名" min-width="180" show-overflow-tooltip />
      <el-table-column prop="description" label="游戏描述" min-width="180" show-overflow-tooltip />
      <el-table-column prop="game_type" label="类型" width="150" align="center">
        <template slot-scope="scope">
          {{ scope.row.game_type === 'dodge_drone' ? '躲避无人机' : scope.row.game_type === 'drive_truck' ? '开货车' : scope.row.game_type }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="240" align="center">
        <template slot-scope="scope">
          <el-button type="text" @click="$router.push(`/mini-games/${scope.row.id}/levels`)">关卡</el-button>
          <el-button type="text" @click="handleSettings(scope.row)">设置</el-button>
          <el-button type="text" style="color: #F56C6C" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Create / Edit Dialog -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="460px"
      append-to-body
    >
      <el-form label-width="100px">
        <el-form-item label="小游戏名">
          <el-input v-model="form.name" placeholder="请输入名称" />
        </el-form-item>
        <el-form-item label="游戏描述">
          <el-input v-model="form.description" type="textarea" :rows="2" placeholder="请输入游戏描述" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="form.game_type" placeholder="请选择类型" style="width: 100%">
            <el-option label="躲避无人机" value="dodge_drone" />
            <el-option label="开货车" value="drive_truck" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getMiniGames, createMiniGame, updateMiniGame, deleteMiniGame, downloadAllMiniGamesAndResources } from "@/net/api";

export default {
  name: "MiniGameList",
  data() {
    return {
      miniGames: [],
      loading: false,
      dialogVisible: false,
      dialogTitle: "新建小游戏",
      saving: false,
      downloading: false,
      form: { id: null, name: "", description: "", game_type: "dodge_drone" }
    };
  },
  created() {
    this.loadMiniGames();
  },
  methods: {
    async loadMiniGames() {
      this.loading = true;
      try {
        const { data } = await getMiniGames();
        this.miniGames = data || [];
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message 
          ? e.response.data.message 
          : "加载失败";
        this.$message.error(msg);
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.form = { id: null, name: "", description: "", game_type: "dodge_drone" };
      this.dialogTitle = "新建小游戏";
      this.dialogVisible = true;
    },
    handleSettings(row) {
      this.form = { id: row.id, name: row.name, description: row.description || "", game_type: row.game_type };
      this.dialogTitle = "小游戏设置";
      this.dialogVisible = true;
    },
    async handleSave() {
      if (!this.form.name.trim()) {
        this.$message.warning("名称不能为空");
        return;
      }
      this.saving = true;
      try {
        if (this.form.id) {
          await updateMiniGame(this.form.id, this.form);
        } else {
          await createMiniGame(this.form);
        }
        this.dialogVisible = false;
        this.$message.success("保存成功");
        this.loadMiniGames();
      } catch (e) {
        this.$message.error("保存失败");
      } finally {
        this.saving = false;
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm("确定删除该小游戏吗？这将删除所有关联关卡。", "提示", { type: "warning" });
        await deleteMiniGame(row.id);
        this.$message.success("删除成功");
        this.loadMiniGames();
      } catch (e) {
        if (e !== "cancel") this.$message.error("删除失败");
      }
    },
    async handleDownloadAll() {
      try {
        this.downloading = true;
        this.$message.info("正在打包下载，请稍候...");
        const { data } = await downloadAllMiniGamesAndResources();
        const urls = data.urls || [];
        if (!urls.length) {
          this.$message.warning("没有可下载的内容");
          return;
        }
        
        for (let i = 0; i < urls.length; i++) {
          const rel = urls[i];
          const href = rel.startsWith("http") ? rel : `${window.location.origin}${rel}`;
          const link = document.createElement("a");
          link.href = href;
          link.download = data.filenames[i] || `minigames_part${i + 1}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          if (urls.length > 1 && i < urls.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        this.$message.success("下载已完成");
      } catch (e) {
        console.error("Download error:", e);
        const msg = e.response && e.response.data && e.response.data.message 
          ? e.response.data.message 
          : (e.message || "未知错误");
        this.$message.error("下载失败: " + msg);
      } finally {
        this.downloading = false;
      }
    }
  }
};
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 15px;
}
</style>
